import { useEffect, useMemo } from 'react';

import { useListContext } from 'react-admin';
import { DefaultValues, FormProvider } from 'react-hook-form';
import { DateRangePickerValue } from 'shared/mui/DatePicker/DateRangePicker/DateRangePicker';
import { FilterContextProvider } from 'shared/mui/Filter/context/filterContext';
import {
  FilterApplyButton,
  FilterButton,
  FilterClearButton,
  FilterResetButton,
} from 'shared/mui/Filter/FilterButtons';
import { FilterChipsToolbar } from 'shared/mui/Filter/FilterChipsToolbar/FilterChipsToolbar';
import {
  FilterDrawer,
  FilterDrawerActionsButtons,
  FilterDrawerContent,
} from 'shared/mui/Filter/FilterDrawer';
import { FilterToolbar } from 'shared/mui/Filter/FilterToolbar/FilterToolbar';
import { useFilters } from 'shared/mui/Filter/hooks/useFilters';
import { useSyncFilter } from 'shared/mui/Filter/hooks/useSyncFilter';
import { UserAction } from 'types/embedded/userAction';
import { cleanEmpty } from 'utils/cleanEmpty';
import { formatInputToTimestamp } from 'utils/date';

import { UserActionsListFilterChips } from './UserActionsListFilterChips';
import { UserActionsListFilterForm } from './UserActionsListFilterForm';

export type UserActionsFilterValues = {
  appliedDate?: [DateRangePickerValue, DateRangePickerValue];
  createdAt?: {
    gte?: number;
    lte?: number;
  };
  user?: string[];
  entityId?: string;
  entityName?: string[];
  roleEntities?: string[];
  eventType?: string[];
};

export const DEFAULT_VALUES: UserActionsFilterValues = {
  user: [],
  entityId: '',
  entityName: [],
  roleEntities: [],
  eventType: [],
  appliedDate: [null, null],
};

type Props = {
  selectedAction?: UserAction;
};

export const UserActionsListFilter: React.FC<Props> = ({ selectedAction }) => {
  const { filterValues } = useListContext();
  const {
    form,
    appliedFilters,
    setAppliedFilter,
    setAppliedFilters,
    onSubmit,
    onReset,
    openDrawer,
    toggleDrawer,
  } = useFilters<UserActionsFilterValues>({
    defaultValues: {
      appliedDate: [
        formatInputToTimestamp(filterValues?.createdAt?.gte),
        formatInputToTimestamp(filterValues?.createdAt?.lte),
      ],
      user: filterValues?.user || [],
      entityId: filterValues?.entityId || '',
      entityName: filterValues?.entityName || [],
      roleEntities: filterValues?.['user.roleEntities'] || [],
      eventType: filterValues?.eventType || [],
    },
    resetValues: DEFAULT_VALUES,
  });

  useSyncFilter({
    appliedFilters,
    transform: (filterValues: DefaultValues<UserActionsFilterValues>): UserActionsFilterValues => {
      const { appliedDate = [null, null, null], roleEntities, ...restFilters } = filterValues;

      return cleanEmpty({
        ...restFilters,
        'user.roleEntities': roleEntities || [],
        createdAt: {
          gte: formatInputToTimestamp(appliedDate?.[0], { unit: 'seconds' }),
          lte: formatInputToTimestamp(appliedDate?.[1], { unit: 'seconds' }),
        },
      });
    },
  });

  useEffect(() => {
    if (selectedAction) {
      setAppliedFilters({
        entityId: selectedAction?.entityId,
        entityName: [selectedAction?.entityName],
      });
    }
  }, [selectedAction]);

  const isFiltersApplied = useMemo(() => Object.keys(filterValues).length > 0, [filterValues]);

  return (
    <>
      <FilterToolbar
        leftActionsSlot={[<FilterButton key="filter" onClick={toggleDrawer(true)} />]}
      />
      <FilterDrawer onClose={toggleDrawer(false)} open={openDrawer}>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <FilterDrawerContent>
              <UserActionsListFilterForm />
            </FilterDrawerContent>
            <FilterDrawerActionsButtons>
              <FilterResetButton onReset={onReset} />
              <FilterApplyButton type="submit" />
            </FilterDrawerActionsButtons>
          </form>
        </FormProvider>
      </FilterDrawer>
      <FilterChipsToolbar appliedFilters={appliedFilters} isSkipBooleanValues={false}>
        <FilterContextProvider appliedFilters={appliedFilters} setAppliedFilter={setAppliedFilter}>
          <FormProvider {...form}>
            <UserActionsListFilterChips />
            <FilterClearButton
              key="clear"
              onClick={() => {
                onReset();
              }}
              visible={isFiltersApplied}
            />
          </FormProvider>
        </FilterContextProvider>
      </FilterChipsToolbar>
    </>
  );
};
